import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search, Clock } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/site/Reveal";
import { getBlogPosts } from "@/lib/api";

const CATEGORY_COLORS = {
  "DevOps": "from-blue-400/30 to-cyan-400/10",
  "AI/ML": "from-purple-400/30 to-indigo-400/10",
  "Case Study": "from-emerald-400/30 to-cyan-400/10",
  "Design": "from-pink-400/30 to-rose-400/10",
  "Cloud": "from-indigo-400/30 to-blue-400/10",
  "Mobile": "from-amber-400/30 to-orange-400/10",
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBlogPosts().then((data) => {
      setPosts(data);
      setLoading(false);
    });
  }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(posts.map((p) => p.category)))], [posts]);

  const filtered = useMemo(() => {
    return posts.filter((p) => {
      const matchesCat = cat === "All" || p.category === cat;
      const matchesQ = !q || p.title.toLowerCase().includes(q.toLowerCase()) || p.excerpt.toLowerCase().includes(q.toLowerCase());
      return matchesCat && matchesQ;
    });
  }, [posts, q, cat]);

  return (
    <div data-testid="blog-page" className="pt-32 pb-20">
      <section className="mx-auto max-w-7xl px-6 md:px-12 mesh-bg pb-12">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
            <Link to="/" className="hover:text-foreground">Home</Link> · Blog
          </p>
          <h1 className="mt-5 font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Notes from the <span className="text-gradient">workshop</span>.
          </h1>
          <p className="mt-7 text-xl text-zinc-400 max-w-2xl">
            Field notes, deep dives, and the occasional rant — written by the people doing the work.
          </p>
        </Reveal>
      </section>

      {/* Filters */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 mt-10">
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <div className="relative md:flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input
              data-testid="blog-search"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles…"
              className="w-full rounded-full glass border-white/10 pl-11 pr-5 py-3 text-sm outline-none focus:border-nexus-cyan/50 transition placeholder:text-zinc-600"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCat(c)}
                className={`rounded-full px-4 py-1.5 text-xs font-medium border transition ${cat === c ? "bg-nexus-cyan text-nexus-ink border-nexus-cyan" : "border-white/10 bg-white/[0.03] text-zinc-300 hover:bg-white/[0.06]"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 mt-12">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0,1,2,3,4,5].map((i) => (
              <div key={i} className="aspect-[4/5] rounded-3xl glass border-white/[0.08] animate-pulse" />
            ))}
          </div>
        ) : (
          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <Item key={p.id}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="block group rounded-3xl glass border-white/[0.08] overflow-hidden h-full flex flex-col">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition duration-700 group-hover:scale-105" />
                    <div className={`absolute inset-0 bg-gradient-to-br ${CATEGORY_COLORS[p.category] || "from-cyan-400/20 to-indigo-400/10"} mix-blend-overlay`} />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center justify-between">
                      <span className="rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-widest text-zinc-300 px-3 py-1">
                        {p.category}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                        <Clock className="h-3 w-3" /> {p.read_time}
                      </span>
                    </div>
                    <h3 className="mt-4 font-display text-2xl tracking-tight leading-tight line-clamp-2 group-hover:text-nexus-cyan transition">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                    <div className="mt-6 flex items-center justify-between pt-5 border-t border-white/[0.06]">
                      <div className="text-xs text-zinc-500">
                        <div className="text-foreground font-medium text-sm">{p.author}</div>
                        <div>{new Date(p.published_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</div>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs text-nexus-cyan">
                        Read <ArrowUpRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              </Item>
            ))}
          </Stagger>
        )}

        {!loading && filtered.length === 0 && (
          <div className="rounded-3xl glass border-white/[0.08] p-16 text-center text-zinc-400">
            No posts found.
          </div>
        )}
      </section>
    </div>
  );
}
